import { lazy, Suspense, useMemo, useState } from "react";

import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Skeleton } from "../../../ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
import { evaluateCell } from "../lib/api/automation-readiness";
import {
  ASSET_TYPES,
  RISK_TIERS,
  type AssetType,
  type AutomationReadinessMatrix,
  type ReadinessThresholds,
  type RiskTier,
} from "../lib/api/types";

const Heatmap = lazy(() => import("./ReadinessHeatmapChart"));

const THRESHOLD_FIELDS: {
  key: keyof ReadinessThresholds;
  label: string;
  step: number;
}[] = [
  { key: "maxExceptionRatePct", label: "Max exception rate (%)", step: 0.5 },
  { key: "maxOverrideRatePct", label: "Max override rate (%)", step: 0.5 },
  { key: "maxMedianDecisionHours", label: "Max median decision time (h)", step: 0.5 },
  { key: "minVolume", label: "Min applications decided", step: 5 },
];

/**
 * Default to an asset type the matrix actually contains, rather than ASSET_TYPES[0].
 *
 * The backend reports assetType "Unknown" for every cell — athena_ue_performance has no asset-type
 * column — so a hardcoded "Construction Equipment" default filters the grid down to nothing and
 * the card renders empty on load, which reads as a broken chart rather than a missing dimension.
 */
function firstPopulatedAssetType(matrix: AutomationReadinessMatrix): AssetType {
  const present = new Set(matrix.cells.map((c) => c.assetType));
  return ASSET_TYPES.find((a) => present.has(a)) ?? ASSET_TYPES[0]!;
}

export function ReadinessMatrix({ matrix }: { matrix: AutomationReadinessMatrix }) {
  const [tier, setTier] = useState<RiskTier>("A");
  const [assetType, setAssetType] = useState<AssetType>(() => firstPopulatedAssetType(matrix));
  // Purely local — nothing here is persisted or sent back to the API.
  const [thresholds, setThresholds] = useState<ReadinessThresholds>(matrix.defaultThresholds);

  const cells = useMemo(
    () =>
      matrix.cells
        .filter((cell) => cell.riskTier === tier && cell.assetType === assetType)
        .map((cell) => ({ ...cell, failingConditions: evaluateCell(cell, thresholds) })),
    [matrix.cells, tier, assetType, thresholds],
  );

  const readyCount = cells.filter((c) => c.failingConditions.length === 0).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Risk Tier</Label>
          <ToggleGroup
            type="single"
            value={tier}
            onValueChange={(value) => value && setTier(value as RiskTier)}
            variant="outline"
            size="sm"
          >
            {RISK_TIERS.map((t) => (
              <ToggleGroupItem key={t} value={t} aria-label={`Risk tier ${t}`} className="px-3">
                {t}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Asset Type</Label>
          <Select value={assetType} onValueChange={(value) => setAssetType(value as AssetType)}>
            <SelectTrigger className="h-9 w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ASSET_TYPES.map((a) => (
                <SelectItem key={a} value={a}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <p className="ml-auto text-xs text-muted-foreground">
          <span className="font-semibold text-approve tabular-nums">{readyCount}</span> of{" "}
          {cells.length} cells meet all four conditions
        </p>
      </div>

      <div className="rounded-lg border border-border bg-secondary/40 p-4">
        <p className="text-xs leading-relaxed text-muted-foreground">
          Readiness thresholds — test different cutoffs here (not saved; real alert thresholds are
          configured separately in Manage Alerts).
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {THRESHOLD_FIELDS.map((field) => (
            <div key={field.key} className="space-y-1.5">
              <Label htmlFor={field.key} className="text-xs text-muted-foreground">
                {field.label}
              </Label>
              <Input
                id={field.key}
                type="number"
                min={0}
                step={field.step}
                value={thresholds[field.key]}
                onChange={(event) =>
                  setThresholds((prev) => ({
                    ...prev,
                    [field.key]: Number(event.target.value),
                  }))
                }
                className="h-9 tabular-nums"
              />
            </div>
          ))}
        </div>
      </div>

      <Suspense fallback={<Skeleton className="h-[340px] rounded-lg" />}>
        <Heatmap cells={cells} />
      </Suspense>
    </div>
  );
}
